import { IonAccordion, IonAccordionGroup, IonBackButton, IonButton, IonButtons, IonCard, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonChip, IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonImg, IonItem, IonLabel, IonMenuButton, IonNote, IonPage, IonRefresher, IonRefresherContent, IonRow, IonTitle, IonToolbar, RefresherEventDetail, useIonAlert, useIonRouter, useIonViewDidEnter, useIonViewWillEnter } from '@ionic/react';
import { addCircleOutline, add, navigateOutline, arrowForwardOutline, refresh, locationOutline, informationCircle } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useAppContext } from '../AppProvider';

const Routes: React.FC = () => {
    const [alertVisible, setAlertVisible] = useState<boolean>(false);
    const router = useIonRouter();
    const [showAlert] = useIonAlert();

    const { networkStatus, getAppStatus, authKey, authenticateMember, authorizedMember, setShowLoader, limits, profiles, settings, savedRoutes, getSavedRoutes, SWTextLogo, SWFullHorizLogo, SWTextDarkLogo, textLogoLoaded, fadeInTextLogo } = useAppContext();
    
    //
    // Authenticate Member
    //
    useIonViewWillEnter(() => { 
        setShowLoader(true);
        const authenticate = async () => {  
            const appstat = await getAppStatus();
            if (!networkStatus && appstat === 'No') {
                router.push('/connection', 'forward');
            } else {
                const authenticated = await authenticateMember();         
                if(!authenticated || authenticated.mt == '') router.push('/login', 'forward');
            }
        };
        authenticate();
    }); 


    useEffect(() => {
        if (limits.routes > 0) {
            const fetchData = async () => {
                await getSavedRoutes(authorizedMember.memberToken, limits.routes);
                window.setTimeout(() => {
                    setShowLoader(false);
                }, 1000);
            }
            fetchData();
        }
    }, [authorizedMember, limits]);
    
    
    const categoryChip = (s: string) => {
        switch(s) {
            case '1': return <IonChip slot='end' color={'primary'}>Air</IonChip>
            case '2': return <IonChip slot='end' color={'tertiary'}>Road</IonChip>
            case '3': return <IonChip slot='end' color={'secondary'}>Trail</IonChip>
            case '4': return <IonChip slot='end' color={'secondary'}>Water</IonChip>           
            default:  return <IonChip slot='end' color={'medium'}>Unknown</IonChip>
        }
    };


    //
    // Modal Confirm for New Route or Trail Form
    //
    const confirmRouteFormStyle = async () => {
        showAlert({
            header: 'Confirm!',
            subHeader: 'Choose either the Universal or Trail form',
            message: 'The Universal form can be used for any type of Route and offers the most amount of options. It is best suited to road travel and air travel. The Trail form is optimized for hiking, mountain biking, and other outdoor activities.',
            buttons: [
                {
                    text: 'Universal',
                    role: 'confirm',
                    cssClass: 'alert-button-confirm',
                    handler: () => { openNewRouteForm('route') }, 
                },
                {
                    text: 'Trail',
                    role: 'confirm',
                    cssClass: 'alert-button-confirm',
                    handler: () => { openNewRouteForm('trail') },
                }
            ]
        })
    };

    const openNewRouteForm = (type: string) => {
        router.push('/account/' + type + '/create', 'forward');
    };
    

    const handleRefresh = (event: CustomEvent<RefresherEventDetail>) => {
        //setLoading(true);
        setTimeout(async () => {
            await getSavedRoutes(authKey.mt, limits.routes);
            event.detail.complete();
            //setLoading(false);
        }, 2000);
    };

    const toggleAlertContent = () => setAlertVisible(!alertVisible);

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot='start'>
                        <IonBackButton color={'tertiary'}>Back</IonBackButton>
                    </IonButtons>
                    <IonTitle><IonImg className={textLogoLoaded ? 'fade-in' : 'fade-out'} src={settings.theme == '1' ? SWTextDarkLogo : SWFullHorizLogo} onIonImgDidLoad={fadeInTextLogo}></IonImg></IonTitle>
                    <IonButtons slot='end'>
                        <IonMenuButton color={'tertiary'} />
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding account-content-bg">

                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent></IonRefresherContent>
                </IonRefresher>     

                <IonGrid>
                    <IonRow>
                        <IonCol size='12' sizeSm='7'>
                            <h1><IonIcon color={'secondary'} icon={navigateOutline}></IonIcon> My Routes<br /><span>Manage your saved routes and trails</span></h1>
                        </IonCol>
                        <IonCol className='ion-margin-bottom top-action-button'  size='12' sizeSm='5'>                            
                            {profiles?.routes < limits?.routes &&
                                <IonButton className='ion-margin' color={'secondary'} onClick={confirmRouteFormStyle}><IonIcon icon={addCircleOutline} slot='start'></IonIcon> Add a new Route</IonButton> 
                            }
                        </IonCol>
                    </IonRow>
                </IonGrid>      

                <IonAccordionGroup className='ion-margin'>
                    <IonAccordion value="info">
                        <IonItem slot='header' color={'light'}>
                            <IonLabel><IonIcon color={'tertiary'} icon={informationCircle} size='large'></IonIcon> What is a SoloWise Route?</IonLabel>
                        </IonItem>
                        <div slot="content">                
                            <IonCard>
                                <IonCardContent>
                                    <p> A <strong>SoloWise Route or Trail</strong> can either start and end at the same SoloWise Place (round-trip) or two different Places (one-way).</p>
                                </IonCardContent>
                            </IonCard>
                        </div>
                    </IonAccordion>
                </IonAccordionGroup>     

                {savedRoutes?.length > 0
                    ? <>
                       {savedRoutes
                            .filter((rte: any) => rte.isSaved == '1')
                            .map((route:any, index:any) => (                    
                                <IonCard key={index} routerDirection='forward' routerLink={route.isTrail == '0' ? `/account/route/edit/${route.token}` : `/account/trail/edit/${route.token}`}>
                                    <IonCardHeader>
                                        <IonCardTitle>{route.name}</IonCardTitle>
                                        <IonCardSubtitle>{route.startPlaceName == '' ? 'Unnamed' : route.startPlaceName} <IonIcon color={'success'} icon={route.type == 'one-way' ? arrowForwardOutline : refresh} size={'large'}></IonIcon> {route.endPlaceName == '' ? 'Unnamed' : route.endPlaceName}</IonCardSubtitle>
                                    </IonCardHeader>                            
                                    <IonCardContent>                                
                                        <p>{route.distance != '' && (route.distance + ' ' + route.distanceUnits + ', ')} {route.type}</p>
                                    </IonCardContent>                            
                                </IonCard>
                        ))}
                        {profiles?.routes >= limits?.routes &&
                            <IonCard>
                                <IonCardHeader>
                                    <IonCardTitle className='ion-margin-bottom ion-text-center' color={'tertiary'}>
                                        You've reached the limit of Routes for your current plan.
                                    </IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent>
                                    <p className='ion-margin-bottom ion-text-center'>
                                        You can add more Routes by upgrading to a SoloWise Premium plan. 
                                    </p>
                                    <p className='ion-margin-bottom ion-text-center'>                            
                                        <IonButton className='ion-margin' color={'secondary'} routerLink='/account/plans' routerDirection='forward'><IonIcon icon={addCircleOutline} slot='start'></IonIcon> Upgrade Now</IonButton>
                                    </p>
                                </IonCardContent>
                            </IonCard>
                        } 
                      </>

                    : <p className='ion-text-center'>You have no recorded Routes.</p>
                }
            </IonContent>
        </IonPage>
    );
};

export default Routes;